import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
    CheckCircle2,
    Download,
    Calendar,
    CreditCard,
    Loader2,
    AlertCircle
} from 'lucide-react'
import { invoiceService, settingsService, clientService } from '../services/firebase'
import { downloadReceiptPDF } from '../services/pdfService'

export default function ReceiptPortal() {
    const { invoiceId } = useParams()
    const [invoice, setInvoice] = useState(null)
    const [client, setClient] = useState(null)
    const [settings, setSettings] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true)
                const inv = await invoiceService.getPublicInvoice(invoiceId)
                if (!inv) {
                    setError('Receipt not found')
                    return
                }
                setInvoice(inv)

                if (inv.userId) {
                    const sett = await settingsService.getPublicSettings(inv.userId)
                    setSettings(sett)
                }

                if (inv.clientId) {
                    const cli = await clientService.getPublicClient(inv.clientId)
                    setClient(cli)
                }
            } catch (err) {
                console.error('Error loading receipt:', err)
                setError('Failed to load receipt details')
            } finally {
                setLoading(false)
            }
        }

        if (invoiceId) {
            loadData()
        }
    }, [invoiceId])

    const handleDownload = async () => {
        try {
            // Calculate total paid
            const totalPaid = invoice.partialAmount || invoice.total
            await downloadReceiptPDF(invoice, client, settings, totalPaid)
        } catch (err) {
            console.error('Download failed:', err)
            alert('Failed to download receipt')
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Loader2 size={40} className="animate-spin text-primary-600" />
            </div>
        )
    }

    if (error || !invoice) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
                    <h1 className="text-xl font-bold text-gray-900 mb-2">Unavailable</h1>
                    <p className="text-gray-500">{error || "Receipt not found."}</p>
                </div>
            </div>
        )
    }

    const totalPaid = invoice.partialAmount || invoice.total
    const lastPaymentDate = invoice.paidAt
        ? (invoice.paidAt.toDate ? invoice.paidAt.toDate() : new Date(invoice.paidAt))
        : new Date()

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4">
            <Helmet>
                <title>Payment Receipt - {invoice.invoiceNumber}</title>
                <meta name="description" content={`Payment receipt for Invoice ${invoice.invoiceNumber}`} />
            </Helmet>

            <div className="max-w-md mx-auto">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    {/* Success Header */}
                    <div className="bg-green-600 p-8 text-center text-white">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                            <CheckCircle2 size={32} className="text-white" />
                        </div>
                        <h1 className="text-2xl font-bold mb-1">Payment Successful</h1>
                        <p className="text-green-100">Thank you for your business!</p>
                    </div>

                    {/* Receipt Details */}
                    <div className="p-8">
                        <div className="text-center mb-8">
                            <p className="text-sm text-gray-500 uppercase tracking-wide font-medium mb-1">Total Amount Paid</p>
                            <p className="text-4xl font-bold text-gray-900">
                                ${totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </p>
                            {invoice.status === 'partial' && (
                                <div className="mt-4 flex flex-col items-center">
                                    <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest mb-1">Remaining Balance</span>
                                    <span className="text-xl font-black text-red-600">
                                        ${(invoice.total - (invoice.partialAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-400 border border-gray-100">
                                        <CreditCard size={18} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Invoice Number</p>
                                        <p className="font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-400 border border-gray-100">
                                        <Calendar size={18} />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 font-medium">Payment Date</p>
                                        <p className="font-semibold text-gray-900">
                                            {lastPaymentDate.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            })}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Merchant Info */}
                        <div className="text-center border-t border-gray-100 pt-6 mb-8">
                            <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-2">Payment To</p>
                            {settings?.logo && (
                                <img
                                    src={settings.logo}
                                    alt={settings.businessName}
                                    className="h-8 w-auto mx-auto mb-2 object-contain"
                                />
                            )}
                            <p className="font-bold text-gray-900">{settings?.businessName || 'Business Name'}</p>
                            <p className="text-sm text-gray-500">{settings?.businessEmail}</p>
                        </div>

                        {/* Action Button */}
                        <button
                            onClick={handleDownload}
                            className="w-full btn btn-primary py-4 text-lg shadow-lg shadow-primary-500/20"
                        >
                            <Download size={20} />
                            Download Receipt PDF
                        </button>
                    </div>
                </div>

                <p className="text-center text-gray-400 text-sm mt-8">
                    Secured by nCinq Invoice
                </p>
            </div>
        </div>
    )
}
